import { useCallback, useState } from 'react';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';

import { Text, Modal } from '@simplywallst/ui-core';

import { useWatchlist } from '@/hooks/useWatchlist';
import type { WatchlistItem } from '@/hooks/useWatchlist';
import { useWatchlistAddFairValue } from '../../hooks/useWatchlistAddFairValue';

import GuidanceList from './GuidanceList';
import { useFairValueContext } from './Context';
import { Error, ModalContent } from './styled';
import { FairValueInput } from './FairValueInput';

const AnalystsModal = loadable(
  () => import('@/pages/CompanyReport/components/AnalystsModal')
);
const DCFRawDataModal = loadable(
  () => import('@/pages/CompanyReport/components/DCFRawData'),
  { resolveComponent: (c) => c.DCFRawDataModal }
);

interface Props {
  company: WatchlistItem['company'];
  defaultUserFairValue: number | null;
}

const FairValueModal = ({ company, defaultUserFairValue }: Props) => {
  const { id: companyId, name, tickerSymbol } = company;
  const { priceTargetAnalystCount } = company.analysisValue;

  const { data: watchlist } = useWatchlist();
  const { state, actions } = useFairValueContext();
  const [userFairValue, setUserFairValue] = useState<number | null>(
    defaultUserFairValue
  );
  const { mutate, status, isError } = useWatchlistAddFairValue({
    onSuccess() {
      actions.closeFairValueModal();
    },
  });
  const { Track } = useTracking({
    type: 'track',
    subject: 'FairValueModal',
    companyId,
  });

  const updateUserFairValue = useCallback((value: number) => {
    setUserFairValue(value);
  }, []);

  const saveUserFairValue = useCallback(() => {
    if (userFairValue !== null && watchlist) {
      mutate({
        companyId,
        fairValue: userFairValue,
        watchlistId: watchlist.id,
      });
    }
  }, [companyId, mutate, userFairValue, watchlist]);

  return (
    <Track>
      {state.isAnalystsModalOpen ? (
        <AnalystsModal
          companyId={companyId}
          companyName={name}
          analystCount={priceTargetAnalystCount ?? 0}
          closeModal={() => actions.openAnalystsModal()}
        />
      ) : null}
      {state.isValuationModalOpen ? (
        <DCFRawDataModal
          companyId={companyId}
          onClose={() => actions.openValuationModal()}
        />
      ) : null}
      <Modal
        title={`Set Fair Value for ${tickerSymbol}`}
        fullscreen
        primaryLabel={'Save'}
        onPrimaryClick={saveUserFairValue}
        primaryLoading={status === 'loading'}
        onClose={() => actions.closeFairValueModal()}
      >
        <ModalContent gap="16px">
          {isError && (
            <Error>
              <Text typography="xSmall" color="white">
                Failed to save your valuation. Please try again later
              </Text>
            </Error>
          )}
          <FairValueInput
            company={company}
            userFairValue={userFairValue}
            disabled={status === 'loading'}
            onUpdate={updateUserFairValue}
          />
          <GuidanceList
            company={company}
            disabled={status === 'loading'}
            onDefaultSelect={updateUserFairValue}
          />
        </ModalContent>
      </Modal>
    </Track>
  );
};

export default FairValueModal;
